<script setup lang="ts">
import toRem from "@/helpers/shared/toRem";
import { useBlurhash } from "@/composables/useBlurhash";
import { computed, ref, toRefs, watch } from "vue";

interface Props {
  src?: string;
  blurhash?: string | null;
  alt: string;
  grayscale?: boolean;
  /**
   * REM
   */
  size?: number;
}

const props = withDefaults(defineProps<Props>(), {
  size: 5,
});

const { alt, size, blurhash, src } = toRefs(props);
const error = ref(false);
const loaded = ref(false);

// Decode blurhash to data URL
const { dataUrl: blurhashDataUrl } = useBlurhash(blurhash);

// Reset loaded and error states when src changes
watch(src, () => {
  loaded.value = false;
  error.value = false;
});

const getFirstChar = (value?: string) => {
  if (!value) {
    return "";
  }

  return value.charAt(0);
};

const initials = computed(() => {
  const parts = alt.value.split(" ");
  const first = parts.at(0);
  const last = parts.at(-1);

  return `${getFirstChar(first)}${getFirstChar(last)}`.trim().toUpperCase();
});

const currentFont = computed(() => {
  if (size.value <= 1.5) return "text-xs";
  if (size.value <= 2) return "text-sm";
  if (size.value <= 3) return "text-base";
  if (size.value <= 4) return "text-lg";
  if (size.value <= 5) return "text-xl";
  if (size.value <= 6) return "text-2xl";
  return "text-3xl";
});
</script>

<template>
  <div class="relative h-fit">
    <div
      class="bg-cover bg-center flex-shrink-0 rounded-full overflow-hidden ring-2 ring-white bg-neutral200 stroke-white stroke-2 shadow-small relative"
      :style="{
        height: toRem(size),
        width: toRem(size),
        minHeight: toRem(size),
        minWidth: toRem(size),
      }"
    >
      <!-- Blurhash placeholder - positioned absolutely to avoid layout shift -->
      <img
        v-if="blurhashDataUrl && src && !loaded && !error"
        :src="blurhashDataUrl"
        :alt="alt"
        class="absolute inset-0 w-full h-full object-cover rounded-full blur-sm"
      />

      <!-- Actual image -->
      <img
        v-if="src && !error"
        :src="src"
        :alt="alt"
        :class="{ grayscale, 'opacity-0': !loaded }"
        loading="lazy"
        class="w-full h-full object-cover rounded-full transition-opacity duration-300"
        @error="error = true"
        @load="loaded = true"
      />

      <!-- Initials fallback -->
      <div
        v-if="!src || error"
        class="b400 w-full h-full flex justify-center items-center text-neutral600 text-md"
        :class="currentFont"
      >
        {{ initials }}
      </div>
    </div>

    <slot v-if="$slots.icon" name="icon"></slot>
  </div>
</template>
